import debugx from "debugx";
import { Position, MapDataSource, Subscription } from "../types";
import { getTrackingService } from "./trackingService";
import { TrackingData } from "./trackingProvider";
import { settingsService } from "./settingsService";

const debug = debugx("bikedash:map-data");

export class MapDataSourceService implements MapDataSource {
  realTrackPoints: Position[] = [];
  smoothedTrackPoints: Position[] = [];
  combinedTrack: Position[] = [];
  currentPosition: Position | null = null;
  lastUpdate: number = 0;
  subscribers: Set<(data: MapDataSource) => void> = new Set();

  private interpolationTimer: number | null = null;
  private lastRealPosition: Position | null = null;
  private trackingSubscription: Subscription | null = null;

  // Predictive interpolation with buffer
  private positionBuffer: Position[] = []; // Buffer of positions for interpolation
  private bufferDelay: number = 1000; // 1 second delay to smooth 1fps GPS updates
  private currentInterpolationTime: number = 0; // Current time in interpolation
  private lastBufferUpdate: number = 0;

  private readonly SMOOTHED_WINDOW_MS = 30 * 1000; // 30 seconds
  private readonly MIN_DISTANCE_THRESHOLD = 2; // meters
  private readonly MAX_BUFFER_SIZE = 10; // Keep last 10 positions in buffer

  private lastKnownMaxUpdateRate: number = 30;

  constructor() {
    debug("MapDataSource constructor called");
    this.setupSettingsListener();
    // Don't set up tracking subscription in constructor to avoid circular dependency
    debug("MapDataSource initialized");
  }

  private setupSettingsListener(): void {
    // Check settings periodically for changes
    setInterval(() => {
      const settings = settingsService.getSettings();
      const smoothingEnabled = settings.map.smoothMotion !== false;
      const currentMaxUpdateRate = settings.user.maxUpdateRate || 30;

      // Check if maxUpdateRate changed
      if (
        currentMaxUpdateRate !== this.lastKnownMaxUpdateRate &&
        this.interpolationTimer
      ) {
        debug(
          "MaxUpdateRate changed: %s → %s fps, restarting interpolation",
          this.lastKnownMaxUpdateRate,
          currentMaxUpdateRate,
        );
        this.lastKnownMaxUpdateRate = currentMaxUpdateRate;
        this.startInterpolation(); // Restart with new rate
        return; // Skip other checks since we're restarting
      }

      // If smoothing was just disabled, stop interpolation
      if (!smoothingEnabled && this.interpolationTimer) {
        debug("Smoothing disabled, stopping interpolation");
        clearInterval(this.interpolationTimer);
        this.interpolationTimer = null;

        // Use the last known position directly
        if (this.lastRealPosition) {
          this.currentPosition = this.lastRealPosition;
          this.notifySubscribers();
        }
      } else if (
        smoothingEnabled &&
        !this.interpolationTimer &&
        this.positionBuffer.length > 0
      ) {
        // Smoothing was enabled and we have data, start interpolation
        debug("Smoothing enabled, starting interpolation");
        this.startInterpolation();
      }
    }, 1000); // Check every second
  }

  public setupTrackingSubscription(): void {
    const trackingService = getTrackingService();

    this.trackingSubscription = trackingService.subscribe(
      (data: TrackingData) => {
        if (data.position && data.isActive) {
          if (data.position.lat && data.position.lng) {
            // Complete position update
            this.addRealPosition(data.position);
          } else if (data.position.compassHeading !== undefined) {
            // Compass-only update (or GPS heading in GPS mode)
            // If we don't have a current position yet, create a placeholder
            if (!this.currentPosition) {
              this.currentPosition = {
                lat: undefined,
                lng: undefined,
                compassHeading: data.position.compassHeading,
                heading: data.position.heading ?? data.position.compassHeading,
                headingUnreliable: data.position.headingUnreliable,
                timestamp: data.position.timestamp || Date.now(),
              };
            } else {
              // Update current position with new compass/GPS heading
              this.updateCompassHeading(
                data.position.compassHeading,
                data.position.timestamp || Date.now(),
                data.position.heading,
                data.position.headingUnreliable,
              );
            }
          }
        } else if (!data.isActive) {
          debug("Tracking stopped or no position data");
        }
      },
    );

    debug("Set up tracking service subscription");
  }

  private compassUpdateCount = 0;
  private lastCompassLogTime = 0;
  private lastCompassEmitTime = 0;
  private lastCompassUpdateRate = 0;

  updateCompassHeading(
    compassHeading: number,
    timestamp: number,
    heading?: number,
    headingUnreliable?: boolean,
  ): void {
    this.compassUpdateCount++;

    // Log actual rate
    if (timestamp - this.lastCompassLogTime >= 1000) {
      debug("MAP DATA SOURCE COMPASS RATE: %s/sec", this.compassUpdateCount);
      this.compassUpdateCount = 0;
      this.lastCompassLogTime = timestamp;
    }

    // Update current position with new compass/GPS heading
    if (this.currentPosition) {
      this.currentPosition = {
        ...this.currentPosition,
        compassHeading,
        heading: heading ?? compassHeading, // Use provided heading or fall back to compassHeading
        headingUnreliable, // Propagate the unreliable flag
        timestamp,
      };

      // Throttle compass updates to match maxUpdateRate
      const settings = settingsService.getSettings();
      const maxUpdateRate = settings.user.maxUpdateRate || 30;
      const minInterval = 1000 / maxUpdateRate; // Convert FPS to milliseconds

      // If update rate changed, reset throttle timer to apply new rate immediately
      if (this.lastCompassUpdateRate !== maxUpdateRate) {
        debug(
          "Update rate changed: %s → %s fps, resetting compass throttle",
          this.lastCompassUpdateRate,
          maxUpdateRate,
        );
        this.lastCompassUpdateRate = maxUpdateRate;
        this.lastCompassEmitTime = 0;
      }

      const now = Date.now();
      const timeSinceLastEmit = now - this.lastCompassEmitTime;

      if (timeSinceLastEmit >= minInterval || this.lastCompassEmitTime === 0) {
        // Enough time has passed, emit the update
        this.lastCompassEmitTime = now;
        this.notifySubscribers();

        debug(
          "Compass heading emitted: %s° (throttled to %s fps)",
          compassHeading.toFixed(1),
          maxUpdateRate,
        );
      } else {
        debug(
          "Compass heading updated but not emitted (throttled): %s°",
          compassHeading.toFixed(1),
        );
      }
    }
  }

  addRealPosition(position: Position): void {
    const now = Date.now();
    debug("📌 addRealPosition called:", {
      lat: position.lat?.toFixed(6),
      lng: position.lng?.toFixed(6),
      timestamp: position.timestamp,
      realTime: now,
      timeDiff: now - position.timestamp,
      hasLat: !!position.lat,
      hasLng: !!position.lng,
    });

    if (!position.lat || !position.lng) {
      debug("Ignoring incomplete position update:", position);
      return;
    }

    const settings = settingsService.getSettings();
    const smoothingEnabled = settings.map.smoothMotion !== false;

    debug("⚙️ Smoothing is:", smoothingEnabled ? "ENABLED" : "DISABLED");

    // DISABLED DISTANCE CHECK FOR NOW - it's preventing interpolation from starting
    // // Check if this position is significantly different from the last one
    // // But always accept the first few positions to bootstrap interpolation
    // if (
    //   this.lastRealPosition &&
    //   this.positionBuffer.length >= 2 &&  // Only check distance after we have some positions
    //   this.calculateDistance(this.lastRealPosition, position) <
    //     this.MIN_DISTANCE_THRESHOLD
    // ) {
    //   debug("Position change too small, skipping:", {
    //     distance: this.calculateDistance(this.lastRealPosition, position),
    //     threshold: this.MIN_DISTANCE_THRESHOLD,
    //     bufferSize: this.positionBuffer.length,
    //   });
    //   return;
    // }

    // Add to real track points
    this.realTrackPoints.push(position);
    this.lastRealPosition = position;
    this.lastUpdate = now;

    // Always update currentPosition with real GPS coordinates
    // This ensures we have valid lat/lng even if we started with compass-only
    // BUT preserve the smoothly interpolated compass heading if we have one
    if (
      !this.currentPosition ||
      !this.currentPosition.lat ||
      !this.currentPosition.lng
    ) {
      const preservedCompassHeading = this.currentPosition?.compassHeading;
      this.currentPosition = {
        ...position,
        // Keep the smoothly interpolated compass heading if we have one
        compassHeading:
          preservedCompassHeading !== undefined
            ? preservedCompassHeading
            : position.compassHeading,
        heading:
          preservedCompassHeading !== undefined
            ? preservedCompassHeading
            : position.heading,
        // Always use the headingUnreliable flag from the position
        headingUnreliable: position.headingUnreliable,
      };
      debug(
        "Set currentPosition from first real GPS update, preserved compass: %s, headingUnreliable: %s",
        preservedCompassHeading,
        position.headingUnreliable,
      );
    }

    if (smoothingEnabled) {
      // Add to position buffer for interpolation
      // BUT strip out heading data - we want to use only compass for heading
      const positionWithoutHeading = {
        ...position,
        heading: undefined,
        compassHeading: undefined,
      };

      // Log timestamp difference for debugging
      const lastBufferPos = this.positionBuffer[this.positionBuffer.length - 1];
      if (lastBufferPos) {
        debug(
          "🕒 Time between GPS updates: %sms",
          position.timestamp - lastBufferPos.timestamp,
        );
      }

      this.positionBuffer.push(positionWithoutHeading);

      // Trim buffer to max size
      if (this.positionBuffer.length > this.MAX_BUFFER_SIZE) {
        this.positionBuffer.shift();
      }

      debug("📦 Added real position to buffer:", {
        lat: position.lat.toFixed(6),
        lng: position.lng.toFixed(6),
        timestamp: position.timestamp,
        accuracy: position.accuracy,
        speed: position.speed,
        bufferSize: this.positionBuffer.length,
        bufferTimestamps: this.positionBuffer.map((p) => p.timestamp),
        totalPoints: this.realTrackPoints.length,
      });

      // Start interpolation only when we have enough positions
      if (!this.interpolationTimer && this.positionBuffer.length >= 2) {
        debug("🎬 Buffer has 2+ positions, starting interpolation timer");
        this.startInterpolation();
      } else if (!this.interpolationTimer) {
        debug(
          "⏳ Waiting for more positions, buffer size: %s",
          this.positionBuffer.length,
        );
      }
    } else {
      // Smoothing disabled - use position directly but preserve compass heading
      if (!this.currentPosition) {
        this.currentPosition = position;
      } else {
        // Preserve compass heading but use new headingUnreliable flag
        this.currentPosition = {
          ...position,
          compassHeading: this.currentPosition.compassHeading,
          heading: this.currentPosition.compassHeading || position.heading,
          headingUnreliable: position.headingUnreliable, // Use new flag from position
        };
      }
      this.smoothedTrackPoints.push(position);

      debug("Using real position directly (smoothing disabled):", {
        lat: position.lat,
        lng: position.lng,
      });

      // Notify subscribers immediately
      this.notifySubscribers();
    }

    // Update combined track
    this.updateCombinedTrack();
  }

  private startInterpolation(): void {
    // Stop any existing interpolation
    if (this.interpolationTimer) {
      clearInterval(this.interpolationTimer);
    }

    // Need at least 2 positions to interpolate
    if (this.positionBuffer.length < 2) {
      debug(
        "Waiting for more positions to start interpolation, current buffer:",
        this.positionBuffer.length,
      );
      return;
    }

    // Reset interpolation time to properly start from first position
    if (this.positionBuffer.length >= 2) {
      // Start slightly after the first position so we can immediately begin interpolating
      // toward the second position
      const firstTime = this.positionBuffer[0].timestamp;
      const secondTime = this.positionBuffer[1].timestamp;
      // Start 10% of the way between first and second position
      this.currentInterpolationTime =
        firstTime + (secondTime - firstTime) * 0.1;
      debug(
        "🚀 Starting interpolation from timestamp:",
        this.currentInterpolationTime,
        "between first:",
        firstTime,
        "and second:",
        secondTime,
        "diff:",
        secondTime - firstTime,
      );
    }

    const settings = settingsService.getSettings();
    const maxUpdateRate = settings.user.maxUpdateRate || 30;
    const interpolationInterval = 1000 / maxUpdateRate; // Convert FPS to milliseconds

    // Track the current update rate so settings listener can detect changes
    this.lastKnownMaxUpdateRate = maxUpdateRate;

    debug("🎬 Starting predictive interpolation:", {
      updateRate: maxUpdateRate,
      interval: interpolationInterval,
      bufferSize: this.positionBuffer.length,
      startTime: this.currentInterpolationTime,
      bufferTimes: this.positionBuffer.map((p) => p.timestamp),
    });

    let frameCount = 0;
    let lastSecond = Date.now();

    // Start interpolation timer at max update rate
    this.interpolationTimer = window.setInterval(() => {
      frameCount++;
      const now = Date.now();

      // Log actual frame rate every second
      if (now - lastSecond >= 1000) {
        debug(
          "📊 Interpolation frame rate: %s fps (target: %s)",
          frameCount,
          maxUpdateRate,
        );
        frameCount = 0;
        lastSecond = now;
      }

      // Check if settings changed
      const currentSettings = settingsService.getSettings();
      const currentRate = currentSettings.user.maxUpdateRate || 30;

      if (currentRate !== maxUpdateRate) {
        // Settings changed, restart with new rate
        debug("Update rate changed, restarting interpolation");
        this.startInterpolation();
        return;
      }

      this.updateInterpolation();
    }, interpolationInterval);
  }

  private updateInterpolation(): void {
    if (this.positionBuffer.length < 2) {
      // Not enough points to interpolate
      debug(
        "Not enough points to interpolate, buffer size:",
        this.positionBuffer.length,
      );
      return;
    }

    const now = Date.now();
    const settings = settingsService.getSettings();
    const maxUpdateRate = settings.user.maxUpdateRate || 30;
    const frameInterval = 1000 / maxUpdateRate; // ms per frame

    // Initialize interpolation time if needed (shouldn't happen since startInterpolation sets it)
    if (this.currentInterpolationTime === 0) {
      const firstPosition = this.positionBuffer[0];
      this.currentInterpolationTime = firstPosition.timestamp;
      debug(
        "⚠️ Late initialization of interpolation time to:",
        this.currentInterpolationTime,
      );
    }

    // Calculate the ideal target time based on buffer delay
    // We want to be playing back positions from (now - bufferDelay) ago
    const idealPlaybackTime = now - this.bufferDelay;

    // Calculate how far behind we are from ideal
    const timeLag = idealPlaybackTime - this.currentInterpolationTime;

    // Adaptive catch-up: if we're too far behind, catch up gradually
    // If we're ahead, slow down
    let timeAdvance = frameInterval;

    if (timeLag > 2000) {
      // We're more than 2 seconds behind, catch up faster
      timeAdvance = frameInterval * 2;
      debug(
        "⏩ Catching up - lag: %sms, advancing by: %sms",
        timeLag,
        timeAdvance,
      );
    } else if (timeLag > 500) {
      // We're a bit behind, catch up slightly
      timeAdvance = frameInterval * 1.2;
      debug(
        "▶️ Slightly behind - lag: %sms, advancing by: %sms",
        timeLag,
        timeAdvance,
      );
    } else if (timeLag < -500) {
      // We're ahead, slow down
      timeAdvance = frameInterval * 0.8;
      debug(
        "⏸️ Too far ahead - lag: %sms, advancing by: %sms",
        timeLag,
        timeAdvance,
      );
    }

    // Advance interpolation time smoothly
    const previousTime = this.currentInterpolationTime;
    this.currentInterpolationTime += timeAdvance;

    // Ensure we're always moving forward
    if (this.currentInterpolationTime <= previousTime) {
      debug(
        "⚠️ Time not advancing! Previous: %s, Current: %s",
        previousTime,
        this.currentInterpolationTime,
      );
      this.currentInterpolationTime = previousTime + frameInterval;
    }

    // Don't get too close to real-time (maintain buffer)
    const latestPosition = this.positionBuffer[this.positionBuffer.length - 1];
    const oldestPosition = this.positionBuffer[0];

    // Don't go past the latest position we have
    const maxTime = Math.min(now - this.bufferDelay, latestPosition.timestamp);

    if (this.currentInterpolationTime > maxTime) {
      debug("⚠️ Clamping interpolation time:", {
        wanted: this.currentInterpolationTime,
        maxTime,
        latestPosTime: latestPosition.timestamp,
        buffer: this.bufferDelay,
      });
      this.currentInterpolationTime = maxTime;
    }

    // Also make sure we're not falling too far behind
    const minTime = oldestPosition.timestamp;
    if (this.currentInterpolationTime < minTime) {
      debug("⚠️ Interpolation time before buffer start, jumping forward:", {
        current: this.currentInterpolationTime,
        minTime,
      });
      this.currentInterpolationTime = minTime + 100; // Start slightly into the buffer
    }

    const targetTime = this.currentInterpolationTime;

    debug("🎯 Interpolation step:", {
      previousTime,
      targetTime,
      advanced: targetTime - previousTime,
      frameInterval,
      bufferStart: this.positionBuffer[0].timestamp,
      bufferEnd: latestPosition.timestamp,
      bufferSize: this.positionBuffer.length,
      maxTime,
      behindRealTime: now - targetTime,
    });

    // Find the two positions to interpolate between
    let beforePos: Position | null = null;
    let afterPos: Position | null = null;

    for (let i = 0; i < this.positionBuffer.length - 1; i++) {
      if (
        this.positionBuffer[i].timestamp <= targetTime &&
        this.positionBuffer[i + 1].timestamp >= targetTime
      ) {
        beforePos = this.positionBuffer[i];
        afterPos = this.positionBuffer[i + 1];
        debug("🎯 Found interpolation window at index %s", i);
        break;
      }
    }

    if (!beforePos || !afterPos) {
      // If we can't find positions to interpolate between
      if (this.positionBuffer.length > 0) {
        if (targetTime <= this.positionBuffer[0].timestamp) {
          // We're at or before the first position, use it directly
          beforePos = afterPos = this.positionBuffer[0];
          debug(
            "🔄 Using first buffer position (target time before buffer start):",
            {
              targetTime,
              firstTime: this.positionBuffer[0].timestamp,
              diff: this.positionBuffer[0].timestamp - targetTime,
            },
          );
        } else if (
          targetTime >=
          this.positionBuffer[this.positionBuffer.length - 1].timestamp
        ) {
          // We're at or past the end, use last position
          beforePos = afterPos =
            this.positionBuffer[this.positionBuffer.length - 1];
          debug(
            "🔄 Using last buffer position (target time after buffer end):",
            {
              targetTime,
              lastTime:
                this.positionBuffer[this.positionBuffer.length - 1].timestamp,
              diff:
                targetTime -
                this.positionBuffer[this.positionBuffer.length - 1].timestamp,
            },
          );
        } else {
          // This shouldn't happen, but log it for debugging
          debug.error("❌ Could not find positions to interpolate between:", {
            bufferLength: this.positionBuffer.length,
            targetTime,
            firstTime: this.positionBuffer[0]?.timestamp,
            lastTime:
              this.positionBuffer[this.positionBuffer.length - 1]?.timestamp,
            allTimes: this.positionBuffer.map((p) => p.timestamp),
          });
          return;
        }
      } else {
        return;
      }
    }

    // Calculate interpolation progress between the two positions
    const timeDiff = afterPos.timestamp - beforePos.timestamp;
    const progress =
      timeDiff > 0 ? (targetTime - beforePos.timestamp) / timeDiff : 0;

    // Allow extrapolation up to 150% for prediction
    const interpolationProgress = Math.max(0, Math.min(1.5, progress));

    // Calculate velocity for better prediction
    let lat: number | undefined;
    let lng: number | undefined;

    if (beforePos.lat && afterPos.lat && beforePos.lng && afterPos.lng) {
      if (interpolationProgress <= 1.0) {
        // Normal LINEAR interpolation between points (no easing for realistic motion)
        lat = this.lerp(beforePos.lat, afterPos.lat, interpolationProgress);
        lng = this.lerp(beforePos.lng, afterPos.lng, interpolationProgress);
        debug("📍 Normal interpolation:", {
          progress: interpolationProgress.toFixed(3),
          from: `${beforePos.lat.toFixed(6)},${beforePos.lng.toFixed(6)}`,
          to: `${afterPos.lat.toFixed(6)},${afterPos.lng.toFixed(6)}`,
          result: `${lat.toFixed(6)},${lng.toFixed(6)}`,
        });
      } else {
        // Extrapolation - predict based on velocity
        const latVelocity = (afterPos.lat - beforePos.lat) / (timeDiff / 1000); // degrees per second
        const lngVelocity = (afterPos.lng - beforePos.lng) / (timeDiff / 1000);
        const extrapolationTime =
          ((interpolationProgress - 1.0) * timeDiff) / 1000; // seconds beyond afterPos

        lat = afterPos.lat + latVelocity * extrapolationTime;
        lng = afterPos.lng + lngVelocity * extrapolationTime;
        debug("🔮 Extrapolating position:", {
          progress: interpolationProgress.toFixed(3),
          velocity: `${latVelocity.toFixed(8)},${lngVelocity.toFixed(8)} deg/s`,
          extraTime: extrapolationTime.toFixed(3),
          result: `${lat.toFixed(6)},${lng.toFixed(6)}`,
        });
      }
    } else {
      lat = beforePos.lat || afterPos.lat;
      lng = beforePos.lng || afterPos.lng;
      debug("⚠️ Missing lat/lng, using fallback");
    }

    // Interpolate position with prediction
    // Use the most recent GPS data for accuracy and speed (don't interpolate these)
    const interpolatedPosition: Position = {
      lat,
      lng,
      timestamp: targetTime,
      isInterpolated: true,
      accuracy: afterPos.accuracy || beforePos.accuracy || 5,
      speed: afterPos.speed || beforePos.speed || 0,
      // Don't interpolate heading - let compass updates handle it
      heading: undefined,
      compassHeading: undefined,
    };

    debug("✅ Interpolation complete:", {
      progress: interpolationProgress.toFixed(2),
      timeWindow: `${beforePos.timestamp} -> ${afterPos.timestamp} (${timeDiff}ms)`,
      targetTime,
      beforePos: `${beforePos.lat?.toFixed(6)},${beforePos.lng?.toFixed(6)}`,
      afterPos: `${afterPos.lat?.toFixed(6)},${afterPos.lng?.toFixed(6)}`,
      result: `${interpolatedPosition.lat?.toFixed(6)},${interpolatedPosition.lng?.toFixed(6)}`,
      isSamePos: beforePos === afterPos,
      hasMovement:
        beforePos.lat !== afterPos.lat || beforePos.lng !== afterPos.lng,
    });

    // Only add position if we have valid lat/lng
    if (interpolatedPosition.lat && interpolatedPosition.lng) {
      this.addSmoothedPosition(interpolatedPosition);
    } else {
      debug("Skipping interpolated position with invalid coordinates");
    }

    // Clean up old positions from buffer (keep only recent ones)
    // Keep at least 3 positions for smooth interpolation
    if (this.positionBuffer.length > 3) {
      const cutoffTime = now - 5000; // Keep last 5 seconds
      const filteredBuffer = this.positionBuffer.filter(
        (pos) => pos.timestamp > cutoffTime,
      );
      // Ensure we keep at least 3 positions
      if (filteredBuffer.length >= 3) {
        this.positionBuffer = filteredBuffer;
      }
    }
  }

  private interpolateAngle(
    angle1: number,
    angle2: number,
    progress: number,
  ): number {
    // Handle angle wrapping (e.g., 359° to 1°)
    let diff = angle2 - angle1;
    if (diff > 180) diff -= 360;
    if (diff < -180) diff += 360;

    let result = angle1 + diff * progress;
    while (result < 0) result += 360;
    while (result >= 360) result -= 360;

    return result;
  }

  private addSmoothedPosition(position: Position): void {
    const now = Date.now();

    // Add the new smoothed position
    this.smoothedTrackPoints.push(position);

    // Clean up old smoothed points (keep only last 30 seconds)
    this.smoothedTrackPoints = this.smoothedTrackPoints.filter(
      (point) => now - point.timestamp <= this.SMOOTHED_WINDOW_MS,
    );

    // Update current position with lat/lng ONLY
    // NEVER touch compass heading here - let compass updates handle that separately
    if (!this.currentPosition) {
      this.currentPosition = position;
    } else {
      // Only update position, preserve compass heading
      this.currentPosition = {
        ...this.currentPosition,
        lat: position.lat,
        lng: position.lng,
        accuracy: position.accuracy,
        speed: position.speed,
        timestamp: position.timestamp,
        isInterpolated: position.isInterpolated,
        // Keep existing compass/heading values
      };
    }

    this.lastUpdate = now;

    debug("Added smoothed position:", {
      lat: position.lat?.toFixed(6),
      lng: position.lng?.toFixed(6),
      smoothedCount: this.smoothedTrackPoints.length,
      isInterpolated: position.isInterpolated,
      notifying: this.subscribers.size,
    });

    // Update combined track and notify
    this.updateCombinedTrack();
    this.notifySubscribers();
  }

  private updateCombinedTrack(): void {
    const now = Date.now();

    // Get real points older than the smoothed window
    const oldRealPoints = this.realTrackPoints.filter(
      (point) => now - point.timestamp > this.SMOOTHED_WINDOW_MS,
    );

    // Combine old real points + recent smoothed points
    this.combinedTrack = [...oldRealPoints, ...this.smoothedTrackPoints].sort(
      (a, b) => a.timestamp - b.timestamp,
    );

    debug("Updated combined track:", {
      oldReal: oldRealPoints.length,
      smoothed: this.smoothedTrackPoints.length,
      total: this.combinedTrack.length,
    });
  }

  private getLastSmoothedPosition(): Position | null {
    return this.smoothedTrackPoints.length > 0
      ? this.smoothedTrackPoints[this.smoothedTrackPoints.length - 1]
      : null;
  }

  private calculateDistance(pos1: Position, pos2: Position): number {
    if (!pos1.lat || !pos1.lng || !pos2.lat || !pos2.lng) return 0;

    const R = 6371e3; // Earth's radius in meters
    const φ1 = (pos1.lat * Math.PI) / 180;
    const φ2 = (pos2.lat * Math.PI) / 180;
    const Δφ = ((pos2.lat - pos1.lat) * Math.PI) / 180;
    const Δλ = ((pos2.lng - pos1.lng) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  private lerp(start: number, end: number, progress: number): number {
    return start + (end - start) * progress;
  }

  private easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  subscribe(callback: (data: MapDataSource) => void): Subscription {
    this.subscribers.add(callback);

    // Immediately call with current data
    callback(this);

    return {
      unsubscribe: () => {
        this.subscribers.delete(callback);
      },
    };
  }

  private notifySubscribers(): void {
    debug(
      "📡 Notifying %s subscribers with position:",
      this.subscribers.size,
      this.currentPosition
        ? `${this.currentPosition.lat?.toFixed(6)},${this.currentPosition.lng?.toFixed(6)}`
        : "null",
    );

    this.subscribers.forEach((callback) => {
      try {
        callback(this);
      } catch (error) {
        debug.error("Error in subscriber callback:", error);
      }
    });
  }

  clear(): void {
    debug("Clearing all track data");

    this.realTrackPoints = [];
    this.smoothedTrackPoints = [];
    this.combinedTrack = [];
    this.currentPosition = null;
    this.lastUpdate = 0;
    this.lastRealPosition = null;

    // Clear interpolation state
    this.positionBuffer = [];
    this.currentInterpolationTime = 0;
    this.lastBufferUpdate = 0;

    if (this.interpolationTimer) {
      clearInterval(this.interpolationTimer);
      this.interpolationTimer = null;
    }

    this.notifySubscribers();
  }

  getStats() {
    return {
      realPoints: this.realTrackPoints.length,
      smoothedPoints: this.smoothedTrackPoints.length,
      combinedPoints: this.combinedTrack.length,
      currentPosition: this.currentPosition,
      lastUpdate: this.lastUpdate,
      isInterpolating: this.interpolationTimer !== null,
    };
  }

  destroy(): void {
    debug("Destroying MapDataSource");

    if (this.interpolationTimer) {
      clearInterval(this.interpolationTimer);
      this.interpolationTimer = null;
    }

    if (this.trackingSubscription) {
      this.trackingSubscription.unsubscribe();
      this.trackingSubscription = null;
    }

    this.subscribers.clear();
    this.clear();
  }
}

// Singleton instance
let mapDataSourceInstance: MapDataSourceService | null = null;

export const getMapDataSource = (): MapDataSourceService => {
  if (!mapDataSourceInstance) {
    debug("Creating new MapDataSourceService instance");
    mapDataSourceInstance = new MapDataSourceService();
    // Set up tracking subscription after instance is created
    mapDataSourceInstance.setupTrackingSubscription();
  }
  return mapDataSourceInstance;
};
